/**
 * Feuille de brouillon — prise en main en quelques étapes.
 *
 * Un élève qui découvre la feuille ne sait pas qu'on écrit en touchant des
 * bouts, ni que chaque ligne est vérifiée. Le tutoriel démarre tout seul à la
 * première ouverture (une fois par appareil), peut être passé à tout moment,
 * et se relance par le petit « ? » de l'en-tête.
 *
 * Chaque étape met en lumière une zone de la feuille (attribut `data-tuto`)
 * et l'explique en une ou deux phrases.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

export interface TutorialStep {
  /** zone mise en lumière (`[data-tuto="…"]` dans la feuille) */
  target: string;
  title: string;
  text: string;
}

/** Les étapes, selon la feuille : la feuille libre n'a ni HORA ni coup de pouce. */
export function tutorialSteps(free: boolean): TutorialStep[] {
  const steps: TutorialStep[] = [
    {
      target: 'bouts',
      title: 'Écris en touchant les bouts',
      text: free
        ? 'Les nombres de la question sont ici, et le pavé de chiffres juste en dessous. Touche-les pour écrire ta ligne.'
        : 'Les nombres, les lettres et les résultats de la question sont ici. Touche-les pour écrire ta ligne.',
    },
    {
      target: 'ops',
      title: 'Les opérations',
      text: 'Elles suivent les bouts : + − × ( ) = … La touche ⌫ efface le dernier bout. Tu peux aussi toucher un bout de ta ligne pour le retirer.',
    },
    {
      target: 'line',
      title: 'Ta ligne est vérifiée',
      text: free
        ? 'Finis par « = » et le résultat : je vérifie les calculs avec des nombres. Ici, pas d’HORA à gagner : la feuille sert à calculer.'
        : 'Finis par « = » et le résultat : je vérifie chaque ligne. Juste et utile, elle se valide et rapporte des HORA. Fausse, je te dis pourquoi.',
    },
    {
      target: 'copy',
      title: 'Recopier',
      text: 'Ce bouton recopie, bout par bout, l’énoncé ou une ligne du dessus. Touche un bout d’une ligne du dessus pour choisir où il commence.',
    },
  ];
  if (!free) {
    steps.push({
      target: 'hint',
      title: 'Coup de pouce',
      text: 'Bloqué ? L’ampoule te montre le prochain pas. Le gain de la ligne est alors divisé par 2.',
    });
  }
  steps.push({
    target: 'close',
    title: 'Ta feuille est gardée',
    text: 'Le ✓ vert, tout au bout, ferme la feuille pour répondre à la question. Rouvre-la quand tu veux : rien n’est perdu. Le « ? » relance cette visite.',
  });
  return steps;
}

/**
 * Exercice « sur la feuille » : la feuille est la page, les réponses sont des
 * touches dans le dock du bas. Visite en 4 étapes (une fois par appareil).
 */
export function paperTutorialSteps(free: boolean): TutorialStep[] {
  return [
    {
      target: 'paper-keys',
      title: 'Tes réponses sont des touches',
      text: 'Touche la réponse que tu choisis. Tu peux changer d’avis : touche une autre touche.',
    },
    {
      target: 'paper-check',
      title: 'Check pour valider',
      text: 'Rien ne part tant que tu n’as pas touché Check.',
    },
    {
      target: 'paper-write',
      title: 'Ton brouillon est sur la feuille',
      text: free
        ? 'Écris tes calculs juste sous la question : je vérifie les calculs avec des nombres.'
        : 'Écris tes calculs juste sous la question : chaque ligne juste et utile rapporte des HORA.',
    },
    {
      target: 'paper-line',
      title: 'Ta ligne en cours',
      text: 'Touche-la pour faire monter les bouts. Le ✓ vert, tout au bout, ramène les touches de réponse.',
    },
    // feuille du moteur v2 (interface du prototype, lot V4) : une seule de ces deux étapes est à l'écran
    {
      target: 'paper-line-v2',
      title: 'Ta ligne en cours',
      text: 'Touche-la pour faire monter le clavier. Touche deux fois une opération pour la sélectionner et choisir son résultat. « Répondre » ramène les touches de réponse.',
    },
  ];
}

const PAPER_SEEN_KEY = 'jr_paper_tuto_seen';

export function hasSeenPaperTutorial(): boolean {
  try {
    return localStorage.getItem(PAPER_SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function markPaperTutorialSeen(): void {
  try {
    localStorage.setItem(PAPER_SEEN_KEY, '1');
  } catch {
    /* stockage indisponible */
  }
}

const SEEN_KEY = 'jr_scratch_tuto_seen';

/** Déjà vu sur cet appareil ? (stockage inaccessible : on ne l'impose pas) */
export function hasSeenScratchTutorial(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function markScratchTutorialSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* stockage indisponible */
  }
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Position de la zone `target`, relative au conteneur (null si absente). */
function useTargetBox(
  container: RefObject<HTMLElement | null>,
  target: string,
  inDocument = false,
): Box | null {
  const [box, setBox] = useState<Box | null>(null);
  const measure = useCallback(() => {
    const root = container.current;
    const el = (inDocument ? document : root)?.querySelector<HTMLElement>(
      `[data-tuto="${target}"]`,
    );
    // dans la page (`inDocument`), le calque couvre l'écran : repère = l'écran,
    // même si le calque n'est pas encore monté (ses refs arrivent après)
    if (!el || (!root && !inDocument)) {
      setBox(null);
      return;
    }
    el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    const r = el.getBoundingClientRect();
    const c = root?.getBoundingClientRect() ?? { top: 0, left: 0 };
    setBox({ top: r.top - c.top, left: r.left - c.left, width: r.width, height: r.height });
  }, [container, target, inDocument]);
  useLayoutEffect(() => {
    measure();
    // un tour plus tard : le calque est monté, la page a fini de défiler
    const raf = requestAnimationFrame(measure);
    const root = container.current ?? (inDocument ? document.body : null);
    if (!root || typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [container, measure, inDocument]);
  return box;
}

const PAD = 6;

export function SheetTutorial({
  container,
  free,
  onDone,
  steps: stepsProp,
  inDocument = false,
}: {
  /** la feuille : c'est là qu'on cherche les zones à mettre en lumière */
  container: RefObject<HTMLElement | null>;
  free: boolean;
  /** fin de la visite (dernière étape ou « Passer ») */
  onDone: () => void;
  /** étapes propres (exercice « sur la feuille ») */
  steps?: TutorialStep[];
  /** chercher les zones dans toute la page (le conteneur est un calque plein écran) */
  inDocument?: boolean;
}) {
  const steps = stepsProp ?? tutorialSteps(free);
  const [i, setI] = useState(0);
  const step = steps[i]!;
  const box = useTargetBox(container, step.target, inDocument);
  const nextRef = useRef<HTMLButtonElement>(null);
  const last = i === steps.length - 1;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    nextRef.current?.focus();
  }, [i]);

  // Échap arrête la visite (sans fermer la feuille)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onDoneRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const height =
    container.current?.clientHeight ??
    (inDocument && typeof window !== 'undefined' ? window.innerHeight : 0);
  // carte sous la zone si elle est dans la moitié haute, sinon au-dessus
  const below = !box || box.top + box.height / 2 < height / 2;
  const cardStyle = box
    ? below
      ? { top: Math.min(box.top + box.height + PAD + 10, Math.max(0, height - 200)) }
      : { bottom: Math.max(height - box.top + PAD + 10, 8) }
    : { top: '35%' };

  return (
    <div className="absolute inset-0 z-[5]" data-testid="scratch-tutorial">
      {box ? (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-2xl ring-2 ring-[var(--sc-gold)] transition-all duration-200"
          style={{
            top: box.top - PAD,
            left: box.left - PAD,
            width: box.width + 2 * PAD,
            height: box.height + 2 * PAD,
            boxShadow: '0 0 0 9999px rgba(3, 10, 30, 0.62)',
          }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0 bg-[rgba(3,10,30,0.62)]" />
      )}
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="scratch-tuto-title"
        className="absolute inset-x-3 mx-auto max-w-md rounded-2xl border border-[var(--sc-line)] bg-[var(--sc-surface)] p-4 shadow-xl lg:inset-x-auto lg:left-1/2 lg:w-[26rem] lg:-translate-x-1/2"
        style={cardStyle}
      >
        <p className="mb-1 text-xs font-semibold text-[var(--sc-muted)]">
          {i + 1} / {steps.length}
        </p>
        <h3
          id="scratch-tuto-title"
          className="font-display text-[16px] font-bold text-[var(--sc-ink)]"
        >
          {step.title}
        </h3>
        <p className="mt-1 text-[14px] leading-snug text-[var(--sc-ink-2)]">{step.text}</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            className="min-h-11 px-2 text-sm font-semibold text-[var(--sc-muted)]"
            onClick={onDone}
          >
            {last ? 'Fermer' : 'Passer'}
          </button>
          <div className="flex gap-2">
            {i > 0 && (
              <button
                type="button"
                className="min-h-11 rounded-full px-3 text-sm font-semibold text-[var(--sc-ink-2)]"
                onClick={() => setI((n) => n - 1)}
              >
                Précédent
              </button>
            )}
            <button
              ref={nextRef}
              type="button"
              className="font-display min-h-11 rounded-full bg-[var(--sc-gold)] px-4 text-sm font-bold text-[var(--sc-on-gold)] active:scale-[0.98]"
              onClick={() => (last ? onDone() : setI((n) => n + 1))}
            >
              {last ? 'C’est parti !' : 'Suivant'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
